import React, { useEffect, useRef, useState } from "react";
import { DataTable, DataTableField } from '../../../../components/data/DataTable';
import { API_ROUTES } from '../../../../core/Constants';
import { hasRole, openJobQuickView, setPage, toPrettyDate, useHub } from '../../../../core/Global';
import Icon from "../../../../components/base/Icon";
import Modal from "../../../../components/base/Modal";
import FlexRow from "../../../../components/base/FlexRow";
import { DrawerForm } from "../../../../components/drawers/DrawerForm";
import PhoneInput from "../../../../components/form/PhoneInput";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import BlockedNumber from "../models/BlockedNumber";


export default function BlockList() {

  const hub = useHub();

  const [deleteModal, deleteModalSet] = useState<boolean>(false);
  const [addDrawer, addDrawerSet] = useState<boolean>(false);
  const [addRows, addRowsSet] = useState<number[]>([0]);
  const rowValues = useRef<Record<number, string>>({});
  const nextRowKey = useRef<number>(1);
  const [selectedItems, selectedItemsSet] = useState<BlockedNumber[]>([]);
  const [refresh, refreshSet] = useState<boolean>();

  useEffect(() => {
    setPage("Block List", "Voice", "PBX Modules");
  }, [])

  useEffect(() => {
    if (refresh) {
      setTimeout(() => refreshSet(false), 1000);
    }
  }, [refresh])


  const NumberItem = (data: BlockedNumber) => (
    <div className='d-flex align-items-center'>
      <Icon icon="ban" type="solid" className="me-2 text-danger" size={14} />
      <div>
        <div className="fs-6 fw-bold">{data.phoneNumber}</div>
        {
          data.displayName &&
          <div className="fs-8 text-muted">{data.displayName}</div>
        }
      </div>
    </div>
  )

  const BlockedItem = (data: BlockedNumber) => (
    <div>
      <div className='d-flex align-items-center'>
        {toPrettyDate(data.createdAt)}
      </div>
      {
        data.blockedByUser &&
        <FlexRow className='fs-8 mt-1'>
          <div>
            <Icon icon="user" className="me-2" size={10} type="solid" />
          </div>
          <div className="fs-8">
            By: {data.blockedByUser.fullName}
          </div>
        </FlexRow>
      }
    </div>
  )

  const ActiveJobItem = (data: BlockedNumber) => (
    data.activeJobId ?
      <a href="#" className="d-flex align-items-center"
        onClick={e => { e.preventDefault(); if (data.activeJobId) openJobQuickView(data.activeJobId); }}>
        <div className="fw-bold text-primary me-2">#{data.activeJobCode}</div>
        {
          data.activeJobAddress &&
          <div className="limited-text text-muted fs-8">{data.activeJobAddress}</div>
        }
        <Icon icon="chevron-right" size={10} className="text-gray-500 ms-2" />
      </a>
      :
      <span className="text-muted fs-8">No active job</span>
  )


  return (
    <div className="p-5">
      <DataTable objectName="Blocked Number"
        addCustom={() => {
          rowValues.current = {};
          nextRowKey.current = 1;
          addRowsSet([0]);
          addDrawerSet(true);
        }}
        delete={hasRole("BlockedNumbersUnblock")}
        route="/voice/pbx/blocklist"
        refresh={refresh}
        apiRoute={API_ROUTES.BlockList}
        deleteCustom={data => {
          selectedItemsSet([data]);
          deleteModalSet(true);
        }}
        deleteMultiple={data => {
          selectedItemsSet(data);
          deleteModalSet(true);
        }}
      >
        <DataTableField title="Number">
          <NumberItem />
        </DataTableField>
        <DataTableField title="Blocked">
          <BlockedItem />
        </DataTableField>
        <DataTableField title="Active Job">
          <ActiveJobItem />
        </DataTableField>
      </DataTable>


      {
        deleteModal && selectedItems.length > 0 &&
        <Modal id="modal-unblock-number"
          title="Unblock Number"
          open visible staticBackdrop
          buttonText="Unblock"
          buttonColor="danger"
          onClose={() => {
            deleteModalSet(false);
            selectedItemsSet([]);
          }}
          onConfirm={callback => {
            const ids = selectedItems.map(e => e.id!);
            hub.Post(API_ROUTES.BlockList + "delete", ids, res => {
              if (res.success) {
                toastSuccess("Selected number(s) have been unblocked.");
              }
              refreshSet(true);
              callback();
            })
          }}>
          <div className="py-5">
            Are you sure you want to unblock the selected number{selectedItems.length > 1 ? "s" : ""}?
          </div>
          <FlexRow className="mb-5">
            {
              selectedItems.map((e, k) =>
                <span key={k} className="badge badge-light-danger me-2">
                  <Icon icon="phone" className="me-2" size={12} />
                  <span className="fs-7 fw-bold">{e.phoneNumber}</span>
                </span>
              )
            }
          </FlexRow>
        </Modal>
      }

      {
        addDrawer &&
        <DrawerForm id="drawer-block-add"
          title="Block Numbers"
          size="medium"
          buttonText="Block Numbers"
          onClose={() => addDrawerSet(false)}
          onSubmit={(body, callback) => {
            const numbers = Array.from(new Set(
              Object.values(rowValues.current).map(v => (v ?? "").trim()).filter(Boolean)
            ));
            if (numbers.length === 0) {
              toastError("Please enter at least one valid phone number.");
              callback?.();
              return;
            }
            hub.Post(API_ROUTES.BlockList + "addmany", { phoneNumbers: numbers }, res => {
              if (res.success) {
                refreshSet(true);
                toastSuccess(`${res.data ?? numbers.length} number(s) have been blocked.`);
              }
              callback?.(res);
            });
          }}>
          <label className="fs-7 fw-500 mb-2">Phone Numbers</label>
          {
            addRows.map(key => (
              <FlexRow key={key} className="mb-2">
                <div className="flex-grow-1">
                  <PhoneInput name={`blockphone_${key}`} parent="drawer-block-add" countryCodes="strict" block noMarginBottom
                    onChange={p => {
                      const raw = (p.phone ?? "").replace(/\D/g, "");
                      rowValues.current[key] = raw.length >= 7 ? ((p.countryCode ?? "+1") + raw) : "";
                    }} />
                </div>
                {
                  addRows.length > 1 &&
                  <a href="#" className="ms-3 text-gray-500" title="Remove"
                    onClick={e => {
                      e.preventDefault();
                      addRowsSet(prev => prev.filter(k => k !== key));
                      delete rowValues.current[key];
                    }}>
                    <Icon icon="trash" size={15} />
                  </a>
                }
              </FlexRow>
            ))
          }
          <a href="#" className="btn btn-sm btn-light-primary mt-2"
            onClick={e => {
              e.preventDefault();
              addRowsSet(prev => [...prev, nextRowKey.current++]);
            }}>
            <Icon icon="plus" className="me-1" size={12} /> Add Number
          </a>
          <div className="text-muted fs-8 mt-4">
            Only allowed country codes are selectable and each number is validated. Add a row per number — they'll all be blocked for this account in one request.
          </div>
        </DrawerForm>
      }

    </div>
  );
}
